/* eslint-disable no-console */

import { register } from 'register-service-worker'

// Track cache busting events
const CACHE_BUST_LOG_PREFIX = '🔄 [CACHE BUST]'
const CACHE_BUST_STORAGE_KEY = 'megaphon_cache_bust_history'
const MAX_STORAGE_ENTRIES = 10 // Keep last 10 events

// Helper function to store cache bust events in localStorage
function storeCacheBustEvent(eventData, shouldLog = true) {
  try {
    const history = JSON.parse(localStorage.getItem(CACHE_BUST_STORAGE_KEY) || '[]')
    
    const event = {
      timestamp: new Date().toISOString(),
      date: new Date().toLocaleString(),
      ...eventData
    }
    
    history.unshift(event) // Add to beginning
    if (history.length > MAX_STORAGE_ENTRIES) {
      history.pop() // Remove oldest
    }
    
    localStorage.setItem(CACHE_BUST_STORAGE_KEY, JSON.stringify(history, null, 2))
    if (shouldLog) {
      console.log(`${CACHE_BUST_LOG_PREFIX} 💾 Event stored in localStorage`)
    }
  } catch (error) {
    if (shouldLog) {
      console.error(`${CACHE_BUST_LOG_PREFIX} ❌ Failed to store in localStorage:`, error)
    }
  }
}

if ((process.env.NODE_ENV === 'production')) {
  register(`${process.env.BASE_URL}service-worker.js`, {
    ready () {
      // Silent - don't log on every page load
      // Only log when there's an actual update (handled in 'updated' callback)
    },
    registered (registration) {
      // Only log on first registration, not on every page load
      try {
        const hasEverRegistered = localStorage.getItem('sw_has_registered')
        if (!hasEverRegistered) {
          // First time registration - log it
          console.log('Service worker has been registered.')
          localStorage.setItem('sw_has_registered', 'true')
          storeCacheBustEvent({
            type: 'Service Worker Registered',
            scope: registration.scope,
            message: 'Service worker registered successfully'
          }, false) // Don't log the storage message
        }
        // Silent on subsequent registrations - this happens on every page load
      } catch (error) {
        // Silent on error
      }
    },
    cached () {
      // Only log on first cache creation, not on every refresh
      try {
        const hasCachedBefore = localStorage.getItem('sw_has_cached')
        if (!hasCachedBefore) {
          console.log('Content has been cached for offline use.')
          localStorage.setItem('sw_has_cached', 'true')
          storeCacheBustEvent({
            type: 'Initial Cache Created',
            message: 'Initial service worker cache created'
          }, false) // Don't log the storage message
        }
        // Silent on subsequent caches
      } catch (error) {
        // Silent on error
      }
    },
    updatefound (registration) {
      console.log(`${CACHE_BUST_LOG_PREFIX} ⚠️ NEW DEPLOYMENT DETECTED! New service worker downloading...`)
      console.log(`${CACHE_BUST_LOG_PREFIX} Old SW:`, registration.installing ? 'Installing new version' : 'Active')
      console.log('New content is downloading.')
      storeCacheBustEvent({
        type: 'New Deployment Detected',
        message: 'Service worker detected new deployment, downloading updates'
      }, false) // Don't log the storage message
    },
    updated (registration) {
      console.log(`${CACHE_BUST_LOG_PREFIX} ✅ NEW VERSION READY! Cache busting in progress...`)
      console.log(`${CACHE_BUST_LOG_PREFIX} Clearing old caches and reloading page in 1 second...`)
      console.log('New content is available; please refresh.')
      
      // Track which assets will be updated
      if (registration.waiting) {
        console.log(`${CACHE_BUST_LOG_PREFIX} Waiting service worker found, activating...`)
      }
      
      storeCacheBustEvent({
        type: 'Cache Bust Triggered',
        message: 'New version ready, cache busting and page reload initiated'
      }, false) // Don't log the storage message
      
      // from gpt::
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      
      // Reload page to apply the new version
      setTimeout(() => {
        console.log(`${CACHE_BUST_LOG_PREFIX} 🚀 RELOADING PAGE WITH FRESH CONTENT...`)
        window.location.reload();
      }, 1000);
    },
    offline () {
      console.log('No internet connection found. App is running in offline mode.')
    },
    error (error) {
      console.error('Error during service worker registration:', error)
      console.error(`${CACHE_BUST_LOG_PREFIX} ❌ Service Worker registration error:`, error)
    }
  })
  
  // Listen for messages from service worker about cache cleanup
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data && event.data.type === 'CACHE_BUST_COMPLETE') {
        console.log(`${CACHE_BUST_LOG_PREFIX} 📨 Received cache bust completion from service worker`)
        storeCacheBustEvent({
          type: 'Service Worker Cache Cleanup',
          cachesDeleted: event.data.cachesDeleted || [],
          cacheStats: event.data.cacheStats || {},
          totalCachesFound: event.data.totalCachesFound || 0,
          message: `Service worker completed cache cleanup: ${event.data.cachesDeleted?.length || 0} old cache(s) deleted`
        }, false) // Don't log the storage message
      }
    })
  }

  // Log when page loads with fresh assets
  window.addEventListener('load', () => {
    const scripts = Array.from(document.querySelectorAll('script[src]'))
    const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
    const hashedAssets = [...scripts, ...styles].filter(el => {
      const src = el.src || el.href
      return src && (src.includes('/js/') || src.includes('/css/'))
    })
    
    // Extract asset hashes
    const assetHashes = hashedAssets.map(el => {
      const src = el.src || el.href
      const match = src.match(/\/([^/]+)\.([a-f0-9]{8})\.[^.]+$/)
      return match ? { name: match[1], hash: match[2], full: match[0] } : { name: src.split('/').pop(), hash: 'unknown', full: src }
    })
    
    if (hashedAssets.length > 0) {
      // Only log and store asset load event if hashes have changed
      try {
        const history = JSON.parse(localStorage.getItem(CACHE_BUST_STORAGE_KEY) || '[]')
        const lastAssetLoad = history.find(event => event.type === 'Assets Loaded')
        
        // Get current hash string for comparison
        const currentHashString = assetHashes.map(a => a.hash).sort().join(',')
        
        // Check if hashes changed
        const hashesChanged = !lastAssetLoad || 
          (lastAssetLoad.fullAssetHashes && 
           lastAssetLoad.fullAssetHashes.map(a => a.hash).sort().join(',') !== currentHashString)
        
        // Only log and store if hashes changed or if it's the first time
        if (hashesChanged) {
          console.log(`${CACHE_BUST_LOG_PREFIX} 📦 Loaded ${hashedAssets.length} hashed assets (JS/CSS)`)
          const hashDisplay = assetHashes.map(a => `${a.name}: ${a.hash}`)
          console.log(`${CACHE_BUST_LOG_PREFIX} Asset hashes:`, hashDisplay)
          
          storeCacheBustEvent({
            type: 'Assets Loaded',
            assetCount: hashedAssets.length,
            assetHashes: assetHashes.map(a => `${a.name}: ${a.hash}`),
            fullAssetHashes: assetHashes,
            hasServiceWorker: !!('serviceWorker' in navigator && navigator.serviceWorker.controller),
            message: `Loaded ${hashedAssets.length} hashed assets after page load`
          }, false) // Don't log the storage message
        }
        // Silent if hashes haven't changed - no logs on normal refresh
      } catch (error) {
        // Fallback: only log/store on first load if we can't check history
        try {
          const fallbackHistory = JSON.parse(localStorage.getItem(CACHE_BUST_STORAGE_KEY) || '[]')
          const hasPreviousAssets = fallbackHistory.some(event => event.type === 'Assets Loaded')
          
          if (!hasPreviousAssets) {
            // First time loading - show logs
            console.log(`${CACHE_BUST_LOG_PREFIX} 📦 Loaded ${hashedAssets.length} hashed assets (JS/CSS)`)
            const hashDisplay = assetHashes.map(a => `${a.name}: ${a.hash}`)
            console.log(`${CACHE_BUST_LOG_PREFIX} Asset hashes:`, hashDisplay)
            
            storeCacheBustEvent({
              type: 'Assets Loaded',
              assetCount: hashedAssets.length,
              assetHashes: assetHashes.map(a => `${a.name}: ${a.hash}`),
              fullAssetHashes: assetHashes,
              hasServiceWorker: !!('serviceWorker' in navigator && navigator.serviceWorker.controller),
              message: `Loaded ${hashedAssets.length} hashed assets after page load`
            }, false) // Don't log the storage message
          }
          // Silent if we've seen assets before - no logs on normal refresh
        } catch (fallbackError) {
          // Completely silent if we can't even read localStorage
        }
      }
    }
    
    // Silent - no tip messages on every page load
  })
}